import React, { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Clear, Search, Visibility } from "@mui/icons-material";

import { useApiClient } from "../../../api";
import { usePagination } from "../../../hooks/usePagination";
import { MyClassesOrAllClassesToggle } from "../../../shared-components/MyClassesOrAllClassesToggle";
import GeneralLoading from "../../../shared-components/Loading";
import { AcademicYearSelect } from "../../../shared-components/AcademicYearSelect";
import { getSubjectsForUser, getYearGroupsForUser } from "../../auth/Utils";

const SEARCH_DEBOUNCE_MS = 200;

export const SchoolClassListItem = ({
  schoolClass,
  onSelect,
  deselect = false,
}) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        p: 1,
        backgroundColor: theme.palette.background.default,
      }}
    >
      <CardContent sx={{ p: 0.5 }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1" fontWeight="medium">
              {schoolClass.name}
            </Typography>

            <Chip
              size="small"
              label={`Year ${schoolClass.yearGroup.yearGroup}`}
            />

            <Chip size="small" label={schoolClass.subject.name} />
          </Stack>

          <Button variant="outlined" onClick={() => onSelect(schoolClass)}>
            {deselect ? "Remove" : "Select"}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

function SchoolClassSummary({ schoolClass, onUnassignClicked, onViewClicked }) {
  const QuickStat = ({ title, value }) => (
    <Grid size={{ xs: 6, md: 3 }}>
      <Typography variant="body2">{title}</Typography>

      <Typography variant="subtitle1" fontWeight="medium">
        {value}
      </Typography>
    </Grid>
  );

  return (
    <Card sx={{ width: "100%" }}>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h5">{schoolClass.name}</Typography>

          {onUnassignClicked && (
            <Button
              endIcon={<Clear />}
              variant="card-action"
              onClick={() => onUnassignClicked(schoolClass)}
            >
              Unassign
            </Button>
          )}
        </Stack>

        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          <Chip label={`Year ${schoolClass.yearGroup.yearGroup}`} />

          <Chip label={schoolClass.subject.name} />
        </Stack>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <QuickStat
            title="Education Level"
            value={schoolClass.educationLevel.level}
          />

          <QuickStat
            title="Academic Year"
            value={schoolClass.academicYear.name}
          />

          <QuickStat
            title="Assignments"
            value={schoolClass.assignments?.length}
          />

          <QuickStat
            title="Essays Marked"
            value={schoolClass.essaysMarkedCount}
          />
        </Grid>

        {onViewClicked && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
              mr: 2,
            }}
          >
            <Button
              startIcon={<Visibility />}
              variant="outlined"
              onClick={() => onViewClicked(schoolClass)}
            >
              View
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export const FruitifySchoolClassSearch = ({
  onClassUnassignClicked = null,
  onSelect = null,
  onClassViewClicked,
  possibleClasses = null,
  listView = false,
  showToggle = true,
  schoolId = null,
  selected = [],
}) => {
  const ApiClient = useApiClient();

  const subjects = getSubjectsForUser();

  const yearGroups = getYearGroupsForUser();

  const [searchTerm, setSearchTerm] = useState("");

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const [selectedSubject, setSelectedSubject] = useState(null);

  const [selectedYearGroup, setSelectedYearGroup] = useState(null);

  const [showAllClasses, setShowAllClasses] = useState(false);

  const [selectedAcademicYear, setSelectedAcademicYear] = useState();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const classFilters = useMemo(
    () => ({
      usersOnly: !showAllClasses,
      showAllClasses,
      school: schoolId,
      searchTerm: debouncedSearchTerm,
      subject: selectedSubject?.id || null,
      yearGroup: selectedYearGroup?.id || null,
      academicYear: selectedAcademicYear || null,
    }),
    [
      showAllClasses,
      schoolId,
      debouncedSearchTerm,
      selectedSubject,
      selectedYearGroup,
      selectedAcademicYear,
    ],
  );

  const {
    options: fetchedSchoolClasses,
    loading,
    page,
    pageInfo,
    handlePageChange,
  } = usePagination(
    ({ first, after }) =>
      ApiClient.schools.getSchoolClasses({
        first,
        after,
        ...classFilters,
      }),
    12,
    classFilters,
    !possibleClasses,
  );

  /*
   * possibleClasses is still a complete
   * local collection, so when supplied we
   * retain client-side filtering.
   */
  const locallyFilteredPossibleClasses = useMemo(() => {
    if (!possibleClasses) {
      return [];
    }

    const normalisedSearch = searchTerm.trim().toLowerCase();

    return possibleClasses.filter((schoolClass) => {
      const matchesSearch =
        !normalisedSearch ||
        schoolClass.name.toLowerCase().includes(normalisedSearch);

      const matchesSubject =
        !selectedSubject || schoolClass.subject.id === selectedSubject.id;

      const matchesYearGroup =
        !selectedYearGroup || schoolClass.yearGroup.id === selectedYearGroup.id;

      const matchesAcademicYear =
        !selectedAcademicYear ||
        schoolClass.academicYear?.id === selectedAcademicYear;

      return (
        matchesSearch &&
        matchesSubject &&
        matchesYearGroup &&
        matchesAcademicYear
      );
    });
  }, [
    possibleClasses,
    searchTerm,
    selectedSubject,
    selectedYearGroup,
    selectedAcademicYear,
  ]);

  const schoolClasses = possibleClasses
    ? locallyFilteredPossibleClasses
    : fetchedSchoolClasses;

  /*
   * Do not show classes which have
   * already been selected.
   */
  const availableClasses = useMemo(
    () =>
      schoolClasses.filter(
        (schoolClass) =>
          !selected.some(
            (selectedClass) => selectedClass?.id === schoolClass.id,
          ),
      ),
    [schoolClasses, selected],
  );

  return (
    <Grid
      container
      spacing={2}
      sx={{
        width: "100%",
        mt: 2,
      }}
      data-testid="fruitify-school-class-search"
    >
      <Grid size={12} sx={{ mt: 2 }}>
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={2}
          sx={{
            width: "100%",
            mb: 2,
          }}
        >
          <TextField
            label="Search Keyword"
            variant="outlined"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{
              width: {
                xs: "100%",
                sm: 280,
              },
            }}
          />

          <AcademicYearSelect
            value={selectedAcademicYear}
            onChange={setSelectedAcademicYear}
            sx={{ width: { xs: "100%", sm: 220 } }}
          />

          <FormControl
            sx={{
              width: {
                xs: "100%",
                sm: 280,
              },
            }}
          >
            <InputLabel id="subject-label">Subject</InputLabel>

            <Select
              labelId="subject-label"
              value={selectedSubject?.id || ""}
              label="Subject"
              onChange={(event) => {
                const subject = subjects.find(
                  (item) => item.id === event.target.value,
                );

                setSelectedSubject(subject || null);
              }}
            >
              <MenuItem value="">All</MenuItem>

              {subjects.map((subject) => (
                <MenuItem key={subject.id} value={subject.id}>
                  {subject.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            sx={{
              width: {
                xs: "100%",
                sm: 280,
              },
            }}
          >
            <InputLabel id="year-label">Year Group</InputLabel>

            <Select
              labelId="year-label"
              value={selectedYearGroup?.id || ""}
              label="Year Group"
              onChange={(event) => {
                const yearGroup = yearGroups.find(
                  (item) => item.id === event.target.value,
                );

                setSelectedYearGroup(yearGroup || null);
              }}
            >
              <MenuItem value="">All</MenuItem>

              {yearGroups.map((yearGroup) => (
                <MenuItem key={yearGroup.id} value={yearGroup.id}>
                  Year {yearGroup.yearGroup}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {showToggle && (
            <MyClassesOrAllClassesToggle
              showAllClasses={showAllClasses}
              setShowAllClasses={setShowAllClasses}
            />
          )}
        </Stack>

        {loading && !possibleClasses ? (
          <GeneralLoading />
        ) : availableClasses.length === 0 ? (
          <Card variant="background">
            <CardContent>
              <Typography variant="subtitle1">No classes found</Typography>
            </CardContent>
          </Card>
        ) : listView ? (
          <Stack direction="column" spacing={1}>
            {availableClasses.map((schoolClass) => (
              <SchoolClassListItem
                key={schoolClass.id}
                schoolClass={schoolClass}
                onSelect={onSelect}
              />
            ))}
          </Stack>
        ) : (
          <Grid container spacing={4}>
            {availableClasses.map((schoolClass) => (
              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
                key={schoolClass.id}
                sx={{ mt: 2 }}
              >
                <SchoolClassSummary
                  schoolClass={schoolClass}
                  onUnassignClicked={onClassUnassignClicked}
                  onViewClicked={onClassViewClicked}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {!possibleClasses && (page > 1 || pageInfo?.hasNextPage) && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              page={page}
              onChange={handlePageChange}
              count={pageInfo.hasNextPage ? page + 1 : page}
              color="primary"
              disabled={loading}
            />
          </Box>
        )}
      </Grid>
    </Grid>
  );
};
